import { type Include, type SurgeryTypeGroup } from '../'

export type SurgeryTypeGroupId = SurgeryTypeGroup['id']
export type SurgeryTypeGroupCode = SurgeryTypeGroup['code']
export type ExternalSurgeryTypeId = number

export const RESOLVED_SURGERY_TYPE_GROUP_INCLUDE = {
    ageRestrictions: true,
    specialities: true,
    subgroups: true,
    surgeryTypes: true,
} as const

export type IncludedSurgeryTypeGroup = Include<SurgeryTypeGroup, typeof RESOLVED_SURGERY_TYPE_GROUP_INCLUDE>

export type ResolvedSurgeryTypeGroup = IncludedSurgeryTypeGroup & {
    resolvedSurgeryTypes: Set<ExternalSurgeryTypeId>
    resolvedChildren: ResolvedSurgeryTypeGroup[]
}

/**
 * Resolves the dips surgery types of each surgery type group by walking the
 * `subgroups` / `surgeryTypes` relations that Storm already eager-loads via
 * {@link RESOLVED_SURGERY_TYPE_GROUP_INCLUDE} — no manual hierarchy/association
 * joins required.
 */
export function resolveSurgeryTypeGroups(surgeryTypeGroups: readonly IncludedSurgeryTypeGroup[]) {
    const resolvedSurgeryTypeGroupsById: Record<SurgeryTypeGroupId, ResolvedSurgeryTypeGroup> = {}
    for (const surgeryTypeGroup of surgeryTypeGroups) {
        resolvedSurgeryTypeGroupsById[surgeryTypeGroup.id] = {
            ...surgeryTypeGroup,
            resolvedSurgeryTypes: new Set(),
            resolvedChildren: [],
        }
    }

    for (const surgeryTypeGroup of Object.values(resolvedSurgeryTypeGroupsById)) {
        surgeryTypeGroup.resolvedChildren = surgeryTypeGroup.subgroups
            .map(child => resolvedSurgeryTypeGroupsById[child.id])
            .filter((child): child is ResolvedSurgeryTypeGroup => child !== undefined)
    }

    const resolved = new Set<SurgeryTypeGroupId>()
    function resolveSurgeryTypes(surgeryTypeGroup: ResolvedSurgeryTypeGroup): Set<ExternalSurgeryTypeId> {
        if (resolved.has(surgeryTypeGroup.id)) return surgeryTypeGroup.resolvedSurgeryTypes
        resolved.add(surgeryTypeGroup.id)

        for (const { external_surgery_type_id } of surgeryTypeGroup.surgeryTypes) {
            surgeryTypeGroup.resolvedSurgeryTypes.add(external_surgery_type_id)
        }
        for (const child of surgeryTypeGroup.resolvedChildren) {
            for (const id of resolveSurgeryTypes(child)) surgeryTypeGroup.resolvedSurgeryTypes.add(id)
        }

        return surgeryTypeGroup.resolvedSurgeryTypes
    }
    for (const surgeryTypeGroup of Object.values(resolvedSurgeryTypeGroupsById)) resolveSurgeryTypes(surgeryTypeGroup)

    const resolvedSurgeryTypeGroupsByCode: Record<SurgeryTypeGroupCode, ResolvedSurgeryTypeGroup> = {}
    const resolvedSurgeryTypeGroupsByHospitalId: Record<ExternalSurgeryTypeId, ResolvedSurgeryTypeGroup[]> = {}
    for (const surgeryTypeGroup of Object.values(resolvedSurgeryTypeGroupsById)) {
        if (surgeryTypeGroup.code) {
            resolvedSurgeryTypeGroupsByCode[surgeryTypeGroup.code] = surgeryTypeGroup
        }
        for (const hospitalId of surgeryTypeGroup.resolvedSurgeryTypes) {
            ;(resolvedSurgeryTypeGroupsByHospitalId[hospitalId] ??= []).push(surgeryTypeGroup)
        }
    }

    return { resolvedSurgeryTypeGroupsById, resolvedSurgeryTypeGroupsByCode, resolvedSurgeryTypeGroupsByHospitalId }
}
